import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

export const Hero: React.FC = () => {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="home" className="min-h-screen flex items-center justify-center relative overflow-hidden bg-slate-900">
      {/* Background Animé avec Points et Lignes */}
      <div className="absolute inset-0">
        {/* Background gradient de base */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-primary-900" />
        
        {/* Points flottants */}
        <div className="absolute inset-0">
          <div className="absolute w-1 h-1 bg-indigo-500 rounded-full opacity-20 animate-float-dots" 
               style={{ top: '20%', animationDelay: '0s', animationDuration: '20s' }} />
          <div className="absolute w-1.5 h-1.5 bg-primary-400 rounded-full opacity-15 animate-float-dots" 
               style={{ top: '40%', animationDelay: '-5s', animationDuration: '25s' }} />
          <div className="absolute w-1 h-1 bg-indigo-400 rounded-full opacity-20 animate-float-dots" 
               style={{ top: '60%', animationDelay: '-10s', animationDuration: '18s' }} />
          <div className="absolute w-1.5 h-1.5 bg-primary-500 rounded-full opacity-15 animate-float-dots" 
               style={{ top: '80%', animationDelay: '-15s', animationDuration: '22s' }} />
          <div className="absolute w-1 h-1 bg-indigo-500 rounded-full opacity-20 animate-float-dots" 
               style={{ top: '10%', animationDelay: '-8s', animationDuration: '30s' }} />
          <div className="absolute w-2 h-2 bg-primary-400 rounded-full opacity-10 animate-float-dots" 
               style={{ top: '70%', animationDelay: '-12s', animationDuration: '16s' }} />
        </div>
        
        {/* Lignes qui traversent */}
        <div className="absolute inset-0">
          <div className="absolute h-px bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-10 animate-float-lines"
               style={{ top: '15%', width: '100px', animationDelay: '0s' }} />
          <div className="absolute h-px bg-gradient-to-r from-transparent via-primary-400 to-transparent opacity-8 animate-float-lines"
               style={{ top: '35%', width: '150px', animationDelay: '-3s' }} />
          <div className="absolute h-px bg-gradient-to-r from-transparent via-indigo-400 to-transparent opacity-10 animate-float-lines"
               style={{ top: '55%', width: '80px', animationDelay: '-6s' }} />
          <div className="absolute h-px bg-gradient-to-r from-transparent via-primary-500 to-transparent opacity-8 animate-float-lines"
               style={{ top: '75%', width: '120px', animationDelay: '-9s' }} />
          <div className="absolute h-px bg-gradient-to-r from-transparent via-indigo-500 to-transparent opacity-10 animate-float-lines"
               style={{ top: '25%', width: '90px', animationDelay: '-4s' }} />
        </div>
        
        {/* Grille subtile */}
        <div className="absolute inset-0 opacity-5">
          <div className="w-full h-full bg-gradient-to-r from-transparent via-indigo-500 to-transparent"
               style={{
                 backgroundImage: `
                   linear-gradient(rgba(99, 102, 241, 0.3) 1px, transparent 1px),
                   linear-gradient(90deg, rgba(99, 102, 241, 0.3) 1px, transparent 1px)
                 `,
                 backgroundSize: '50px 50px',
                 animation: 'gridMove 20s linear infinite'
               }} />
        </div>
        
        {/* Overlay gradient léger */}
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-600/5 to-primary-600/5" />
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center">
          {/* Badge moderne */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <div className="inline-block px-4 py-2 bg-indigo-500/10 border border-indigo-500/20 rounded-full backdrop-blur-sm">
              <span className="text-indigo-400 font-medium text-sm">✨ Développeur Full Stack</span>
            </div>
          </motion.div>

          {/* Main heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="text-5xl md:text-7xl font-bold mb-6"
          >
            <span className="bg-gradient-to-r from-indigo-400 to-primary-400 bg-clip-text text-transparent">
              Enzo
            </span>
            <br />
            <span className="text-slate-100">Marques</span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="text-xl md:text-2xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed"
          >
            Créateur d'expériences numériques - À la recherche d'une alternance dès septembre 2025 avec{' '}
            <span className="font-semibold text-indigo-400">React</span> &{' '}
            <span className="font-semibold text-primary-400">JavaScript</span>
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <motion.button
              onClick={() => scrollToSection('projects')}
              className="px-8 py-4 bg-gradient-to-r from-indigo-600 to-primary-600 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-indigo-500/25 transition-all duration-300 relative overflow-hidden group"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <span className="relative z-10">Voir mes projets</span>
              <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
            </motion.button>
            
            <motion.button
              onClick={() => scrollToSection('contact')}
              className="px-8 py-4 border-2 border-indigo-500/50 text-indigo-400 rounded-xl font-semibold hover:bg-indigo-500/10 hover:border-indigo-400 transition-all duration-300 backdrop-blur-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Me contacter
            </motion.button>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 cursor-pointer"
        onClick={() => scrollToSection('about')}
      >
        <ChevronDown className="text-slate-400 hover:text-indigo-400 transition-colors" size={32} />
      </motion.div>
    </section>
  );
};